import { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Stack,
  TextField,
  Button,
  IconButton,
  Tooltip,
  useTheme,
} from "@mui/material";
import { Pencil } from "lucide-react";

import { useTranslation } from "../../language";
import CustomDialogSmall from "../Dialog/DiaCompSmall";
import * as c from "../../constants";
import { alpha } from "@mui/material/styles";

/* labelKey/promptKey/emptyKey: translation keys for the caller's context-specific copy
   compact: plain address block with the edit pencil on the right — no box, label or prompt text */
export default function ShippingAddressEditor({ labelKey, promptKey, emptyKey, compact = false }) {
  const theme = useTheme();
  const { t } = useTranslation();

  const [address, setAddress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: "", line1: "", line2: "", city: "", postal_code: "", country: "" });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    let mounted = true;

    axios
      .get(`${c.API_URL}/api/addresses/`)
      .then((res) => {
        if (!mounted) return;
        const addresses = res.data || [];
        const found =
          addresses.find((a) => a.address_type === "shipping") ||
          addresses.find((a) => a.address_type === "billing") ||
          null;

        setAddress(found);
        if (found) {
          setForm({
            name: found.name || "",
            line1: found.line1 || "",
            line2: found.line2 || "",
            city: found.city || "",
            postal_code: found.postal_code || "",
            country: found.country || "",
          });
        }
      })
      .catch(() => {
        if (mounted) setAddress(null);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const handleFieldChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleOpenDialog = () => {
    setErrors({});
    setSaveError(null);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setErrors({});
    setSaveError(null);

    try {
      const res = await axios.put(`${c.API_URL}/api/addresses/shipping/`, form);
      setAddress(res.data);
      setDialogOpen(false);
    } catch (err) {
      if (err.response?.status === 400) {
        setErrors(err.response.data || {});
      } else {
        setSaveError(t("address_save_error"));
      }
    } finally {
      setSaving(false);
    }
  };

  const isDark = theme.palette.mode === "dark";
  const boxSx = {
    mt: 1.5,
    p: 2,
    borderRadius: 2,
    bgcolor: isDark ? alpha("#fff", 0.04) : alpha("#fff", 0.5),
    backdropFilter: "blur(28px) saturate(180%)",
    WebkitBackdropFilter: "blur(28px) saturate(180%)",
    border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.65)"}`,
    boxShadow: isDark
      ? "inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 32px rgba(0,0,0,0.55)"
      : "inset 0 1px 0 rgba(255,255,255,0.7), 0 4px 24px rgba(0,0,0,0.06)",
  };

  if (loading) {
    return (
      <Box sx={{ ...(compact ? {} : boxSx), display: "flex", justifyContent: "center" }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  const addressLines = address ? (
    <>
      <Typography variant="body2" fontWeight={600}>{address.name}</Typography>
      <Typography variant="body2" color="text.secondary">{address.line1}</Typography>
      {address.line2 && <Typography variant="body2" color="text.secondary">{address.line2}</Typography>}
      <Typography variant="body2" color="text.secondary">
        {address.postal_code} {address.city}
      </Typography>
      <Typography variant="body2" color="text.secondary">{address.country}</Typography>
    </>
  ) : (
    <Typography variant="body2" color="text.secondary">{t(emptyKey)}</Typography>
  );

  return (
    <Box sx={compact ? {} : boxSx}>
      {compact ? (
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>{addressLines}</Box>
          <Tooltip title={t("address_edit")}>
            <IconButton onClick={handleOpenDialog}>
              <Pencil size={22} color={theme.palette.primary.main} />
            </IconButton>
          </Tooltip>
        </Stack>
      ) : (
      <>
      <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
        {t(labelKey)}
      </Typography>

      {address ? (
        <>
          <Typography variant="body2" fontWeight={600}>{address.name}</Typography>
          <Typography variant="body2" color="text.secondary">{address.line1}</Typography>
          {address.line2 && <Typography variant="body2" color="text.secondary">{address.line2}</Typography>}
          <Typography variant="body2" color="text.secondary">
            {address.postal_code} {address.city}
          </Typography>
          <Typography variant="body2" color="text.secondary">{address.country}</Typography>
        </>
      ) : (
        <Typography variant="body2" color="text.secondary">{t(emptyKey)}</Typography>
      )}

      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 1.5 }}>
        <Typography variant="body2" color="text.secondary">
          {t(promptKey)}
        </Typography>
        <Tooltip title={t("address_edit")}>
          <IconButton size="small" onClick={handleOpenDialog}>
            <Pencil size={16} color={theme.palette.secondary.main} />
          </IconButton>
        </Tooltip>
      </Stack>
      </>
      )}

      <CustomDialogSmall
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={t("address_edit")}
        content={
          <Stack spacing={1.5}>
            <TextField
              size="small"
              fullWidth
              label={t("address_name")}
              value={form.name}
              onChange={handleFieldChange("name")}
              error={!!errors.name}
              helperText={errors.name?.[0]}
            />
            <TextField
              size="small"
              fullWidth
              label={t("address_line1")}
              value={form.line1}
              onChange={handleFieldChange("line1")}
              error={!!errors.line1}
              helperText={errors.line1?.[0]}
            />
            <TextField
              size="small"
              fullWidth
              label={t("address_line2")}
              value={form.line2}
              onChange={handleFieldChange("line2")}
            />
            <Stack direction="row" spacing={1.5}>
              <TextField
                size="small"
                fullWidth
                label={t("address_postal_code")}
                value={form.postal_code}
                onChange={handleFieldChange("postal_code")}
                error={!!errors.postal_code}
                helperText={errors.postal_code?.[0]}
              />
              <TextField
                size="small"
                fullWidth
                label={t("address_city")}
                value={form.city}
                onChange={handleFieldChange("city")}
                error={!!errors.city}
                helperText={errors.city?.[0]}
              />
            </Stack>
            <TextField
              size="small"
              fullWidth
              label={t("address_country")}
              value={form.country}
              onChange={handleFieldChange("country")}
              error={!!errors.country}
              helperText={errors.country?.[0]}
            />
            {saveError && <Alert severity="error">{saveError}</Alert>}
          </Stack>
        }
        action={
          <Button
            variant="outlined"
            size="large"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
            sx={{
              minWidth: { xs: 80, sm: 90, md: 100, lg: 100, xl: 100 },
              transition: "transform 0.2s ease",
              ...(theme.palette.mode === "light"
                ? {
                    py: 1.5,
                    color: "common.white",
                    bgcolor: "greydark.C900",
                    borderColor: "greydark.C900",
                    borderRadius: 7,
                    "&:hover": {
                      color: "common.white",
                      bgcolor: "greydark.C900",
                      borderColor: "greydark.C900",
                      borderRadius: "20px",
                      transform: "scale(1.2)",
                    },
                    "&.Mui-disabled": {
                      color: "grey.600",
                      bgcolor: "grey.300",
                      borderColor: "grey.300",
                    },
                  }
                : {
                    p: "10px 14px",
                    color: "common.black",
                    bgcolor: "grey.C200",
                    borderColor: "grey.C200",
                    borderRadius: "20px",
                    "&:hover": {
                      color: "common.black",
                      bgcolor: "grey.C200",
                      borderColor: "grey.C200",
                      transform: "scale(1.2)",
                    },
                    "&.Mui-disabled": {
                      color: "rgba(255, 255, 255, 0.5)",
                      bgcolor: "rgba(255, 255, 255, 0.12)",
                      borderColor: "rgba(255, 255, 255, 0.12)",
                    },
                  }),
            }}
          >
            {t("address_save")}
          </Button>
        }
      />
    </Box>
  );
}
